import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { DatetimeChangeEventDetail, ModalController } from "@ionic/angular";
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { StaticClass } from 'src/components/shared-components/class/general-classes/static-classes';
import { ConnectionType } from 'src/components/shared-components/enum/enumSettings';
import { iEvent } from 'src/components/shared-components/interfaces/iEvent';
import { EventService } from 'src/service/event.service';
import { NetworkService } from 'src/service/network.service';

/**
 * Modal that lets the user pick either a date or a time through a single, non-nested
 * `ion-datetime`. Presentation is driven by the {@link isDate}/{@link isTime} inputs.
 *
 * On confirm it dismisses with `{ time: "HH:mm", date: <ISO 8601 string> }`; callers read
 * whichever field they requested (`data.time` for the time picker, `data.date` for the date
 * picker). On cancel/backdrop it dismisses with no data.
 */
@Component({
    selector: 'app-time-selector',
    templateUrl: './time-selector.component.html',
    styleUrls: ['./time-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TimeSelectorComponent implements OnInit, OnDestroy, iEvent {
  /** ISO 8601 date used to seed the picker when in date mode. */
  @Input() actualStartDatePicker: string = "";
  /** "HH:mm" time used to seed the picker when in time mode. */
  @Input() actualStartTimePicker: string = "";
  @Input() isDate: boolean = false;
  @Input() isTime: boolean = false;
  /** ISO 8601 lower bound for the date picker (ignored when empty). */
  @Input() minDate: string = "";
  /** ISO 8601 upper bound for the date picker (ignored when empty). */
  @Input() maxDate: string = "";

  /** "HH:mm" currently selected in time mode. */
  private _timeSelected: string = "";
  /** ISO 8601 date currently selected in date mode. */
  private _dateSelected: string = "";
  private _networkSubscription: Subscription = null;
  public _eventSubscriptions: Array<Subscription> = null;

  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public toolBarColor: string = "primary";

  /** ISO 8601 value bound to the `ion-datetime` (`value` must be a full ISO string). */
  public pickerValue: string = null;

  constructor(
    private _modalCtrl: ModalController,
    private _networkService: NetworkService,
    private _eventService: EventService,
    private _translateService: TranslateService
  ) { }

  ngOnInit() {
    this._timeSelected = this.actualStartTimePicker;
    this._dateSelected = this.actualStartDatePicker;
    this.initPickerValue();
    this.subsribeNetworkServiceData();
    this.subscribeEvents();
  }

  ngOnDestroy(): void {
    this._networkSubscription?.unsubscribe();
    this.unsubscribeEvents();
  }

  /** `ion-datetime` presentation to render for the requested mode. */
  public get presentation(): "date" | "time" {
    return this.isDate ? "date" : "time";
  }

  /** Locale used to render the picker, following the active app language. */
  public get locale(): string {
    return this._translateService.getCurrentLang() || "it-IT";
  }

  /**
   * Build the ISO string fed to `ion-datetime`. In time mode the "HH:mm" input is wrapped in a
   * throw-away date so it is a valid ISO value; in date mode the incoming ISO string is used as-is.
   */
  private initPickerValue(): void {
    if (this.isTime) {
      const [hh, mm] = this.extractHoursMinutes(this.actualStartTimePicker);
      this._timeSelected = `${hh}:${mm}`;
      this.pickerValue = `1970-01-01T${hh}:${mm}:00`;
    } else {
      this.pickerValue = this.actualStartDatePicker || null;
    }
  }

  /**
   * Extract a zero-padded `[HH, mm]` pair from a time-bearing string. Accepts a bare "HH:mm",
   * a "dd-MM-yyyy HH:mm:ss" string or an ISO datetime; falls back to "00:00" when unparsable.
   * @param value time-bearing string
   * @returns tuple of zero-padded hours and minutes
   */
  private extractHoursMinutes(value: string): [string, string] {
    let time: string = (value || "").trim();
    if (time.includes("T")) {
      time = time.split("T")[1];
    } else if (time.includes(" ")) {
      time = time.split(" ").pop();
    }
    const [rawH = "0", rawM = "0"] = time.split(":");
    const hh: string = String(Number(rawH) || 0).padStart(2, "0");
    const mm: string = String(Number(rawM) || 0).padStart(2, "0");
    return [hh, mm];
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalCtrl.dismiss();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  private subsribeNetworkServiceData(): void {
    this._networkSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  /**
   * Handle a selection from the inner `ion-datetime`. Stores the ISO date (date mode) or the
   * "HH:mm" time (time mode) so it can be returned on confirm.
   * @param event native `ionChange` event carrying the selected ISO value
   */
  public onDateTimeChange(event: CustomEvent<DatetimeChangeEventDetail>): void {
    const value = event.detail.value;
    if (!value || Array.isArray(value)) {
      return;
    }
    if (this.isTime) {
      const [hh, mm] = this.extractHoursMinutes(value);
      this._timeSelected = `${hh}:${mm}`;
    } else {
      this._dateSelected = value;
    }
  }

  /**
   * Return the selected value to the caller.
   */
  public onClickConfirm() {
    if (this._timeSelected || this._dateSelected) {
      this._modalCtrl.dismiss({
        time: this._timeSelected,
        date: this._dateSelected,
      });
    } else {
      this._modalCtrl.dismiss();
    }
  }

  /**
   * Exit without press confirm
   */
  public onClickCancel() {
    this._modalCtrl.dismiss();
  }
}
